import { type PaginationState } from "@tanstack/react-table";
import { useState } from "react";
import { type PageSize, INITIAL_PAGE_SIZE } from "./usePagination";

export type ClientPaginationProperties = ReturnType<typeof useClientPagination>;

/**
 * Calculates the total number of pages based on total count and page size.
 * Returns at least 1 page even when totalCount is 0.
 *
 * @param totalCount - Total number of items
 * @param pageSize - Number of items per page
 * @returns Total number of pages (minimum 1)
 */
export const calculateTotalPages = (totalCount: number, pageSize: number): number => {
  return Math.max(1, Math.ceil(totalCount / pageSize));
};

/**
 * Gets the next page index, ensuring it doesn't exceed the maximum page index.
 *
 * @param currentPageIndex - Current zero-based page index
 * @param totalPages - Total number of pages
 * @returns Next page index (capped at totalPages - 1)
 */
export const getNextPageIndex = (currentPageIndex: number, totalPages: number): number => {
  return Math.min(currentPageIndex + 1, totalPages - 1);
};

/**
 * Gets the previous page index, ensuring it doesn't go below 0.
 *
 * @param currentPageIndex - Current zero-based page index
 * @returns Previous page index (minimum 0)
 */
export const getPreviousPageIndex = (currentPageIndex: number): number => {
  return Math.max(currentPageIndex - 1, 0);
};

/**
 * Hook for client-side pagination management.
 * Handles pagination state, page navigation, and page size changes.
 *
 * @param options - Configuration object
 * @param options.totalCount - Total number of items to paginate
 * @param options.initialPageSize - Initial page size (defaults to INITIAL_PAGE_SIZE)
 * @returns Pagination properties including state, controls, and metadata
 */
export const useClientPagination = ({
  totalCount,
  initialPageSize = INITIAL_PAGE_SIZE,
}: {
  totalCount: number;
  initialPageSize?: PageSize;
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: Number(initialPageSize),
  });

  const totalPages = calculateTotalPages(totalCount, pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  const handleGoToFirstPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleGoToLastPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: totalPages - 1 }));
  };

  const handleGoToNextPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: getNextPageIndex(prev.pageIndex, totalPages) }));
  };

  const handleGoToPreviousPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: getPreviousPageIndex(prev.pageIndex) }));
  };

  const handleSetPageSize = (newPageSize: PageSize) => {
    setPagination(() => ({
      pageIndex: 0, // Reset to first page when changing page size
      pageSize: Number(newPageSize),
    }));
  };

  const handleResetPagination = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const isPreviousPageDisabled = pagination.pageIndex <= 0;
  const isNextPageDisabled = pagination.pageIndex >= totalPages - 1;

  return {
    type: "client" as const,
    paginationState: pagination,
    setPagination,
    paginationQueryVariables: {
      first: pagination.pageSize,
      last: null,
      after: null,
      before: null,
    },
    paginationInfo: {
      pageSize: pagination.pageSize.toString() as PageSize,
      totalCount,
      totalPages,
      currentPage,
    },
    controls: {
      handleResetPagination,
      handleGoToFirstPage,
      handleGoToLastPage,
      handleGoToNextPage,
      handleGoToPreviousPage,
      handleSetPageSize,
    },
    flags: {
      isNextPageDisabled,
      isPreviousPageDisabled,
    },
  };
};
